import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { venueImageUrl } from '../../api';
import { useGeolocation } from '../../hooks/useGeolocation';
import { useLocationFilter } from '../../hooks/useLocationFilter';
import LocationFilter from '../../components/LocationFilter';
import { nativeShare, siteOrigin } from '../../native';
import { trackEvent } from '../../analytics';
import { InviteToHangoutButton } from '../hangouts/components/InviteParts';
import '../hangouts/hangouts.css';
import { eventPlace, eventWhen, getEvent, getEvents, getVenueEvents, PublicEvent } from './api';
import './events.css';

const FALLBACK_POSTER = '/hero.jpg';
const poster = (e: PublicEvent) => venueImageUrl(e.posterUrl) ?? FALLBACK_POSTER;

function DateBlock({ iso }: { iso: string }) {
  const d = new Date(`${iso}T00:00:00`);
  return (
    <div className="event-date" aria-hidden="true">
      <b>{d.getDate()}</b>
      <span>{d.toLocaleDateString('en-IN', { month: 'short' }).toUpperCase()}</span>
    </div>
  );
}

// /events: upcoming events near the visitor or in the chosen State / District, soonest first.
export function EventsList() {
  const { coords, resolved } = useGeolocation();
  const { area, label } = useLocationFilter();
  const [events, setEvents] = useState<PublicEvent[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [category, setCategory] = useState('All');

  useEffect(() => {
    if (!resolved) return;
    let current = true;
    setFailed(false);
    setEvents(null);
    getEvents(coords.lat, coords.lng, area)
      .then((list) => current && setEvents(list))
      .catch(() => current && setFailed(true));
    return () => {
      current = false;
    };
  }, [resolved, coords.lat, coords.lng, area]);

  // Category chips are the categories actually listed right now.
  const categories = ['All', ...Array.from(new Set((events ?? []).map((e) => e.category)))];
  const shown = (events ?? []).filter((e) => category === 'All' || e.category === category);

  return (
    <main className="container section">
      <LocationFilter />
      {categories.length > 1 && (
        <div className="filters">
          {categories.map((c) => (
            <button key={c} type="button" className={`filter ${c === category ? 'on' : ''}`} aria-pressed={c === category} onClick={() => setCategory(c)}>{c}</button>
          ))}
        </div>
      )}
      {failed ? (
        <div className="panel"><p>Couldn't load events right now. Please refresh the page.</p></div>
      ) : events === null ? (
        <p className="muted">Loading...</p>
      ) : shown.length === 0 ? (
        <div className="panel">
          <p className="muted">{events.length === 0 ? `No upcoming events ${area.state ? `in ${label}` : 'near you'} yet. Try another state or district, or check back soon!` : 'No events in this category.'}</p>
        </div>
      ) : (
        <div className="grid">
          {shown.map((e) => (
            <article className="card event-card" key={e.id}>
              <Link to={`/events/${e.id}`} className="card-image" aria-label={e.title}>
                <img src={poster(e)} alt="" loading="lazy" />
                <span className="badge">{e.category.toUpperCase()}</span>
                {e.status === 'Today' && <span className="badge pink" style={{ left: 'auto', right: 10 }}>TONIGHT</span>}
              </Link>
              <div className="card-body event-card-body">
                <DateBlock iso={e.eventDate} />
                <div>
                  <h3>{e.title}</h3>
                  <p className="muted">{e.venueName} · {eventPlace(e)}{e.distanceKm != null ? ` · ${e.distanceKm} km` : ''}</p>
                  <p>{eventWhen(e)}</p>
                  {e.entryInfo && <p className="gold event-entry">{e.entryInfo}</p>}
                  <Link className="btn btn-gold" to={`/events/${e.id}`}>View Details</Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </main>
  );
}

// /events/:id: full details, with directions, sharing and inviting friends to the venue.
export function EventDetails() {
  const { id } = useParams<{ id: string }>();
  const eventId = Number(id);
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setEvent(null);
    setNotFound(false);
    getEvent(eventId)
      .then((e) => {
        setEvent(e);
        trackEvent('event_viewed', { event_id: e.id, category: e.category });
      })
      .catch(() => setNotFound(true));
  }, [eventId]);

  if (notFound) {
    return (
      <main className="container section">
        <div className="panel" style={{ textAlign: 'center' }}>
          <h2>Event not found</h2>
          <p className="muted">It may have been removed by the venue.</p>
          <Link className="btn btn-gold" to="/events">See all events</Link>
        </div>
      </main>
    );
  }
  if (!event) return <main className="container section"><p className="muted">Loading event...</p></main>;

  const url = `${siteOrigin()}/events/${event.id}`;
  const shareText = `${event.title} at ${event.venueName}, ${eventWhen(event)}. ${url}`;
  const share = async () => {
    trackEvent('event_shared', { event_id: event.id });
    if (await nativeShare({ title: event.title, text: shareText, url })) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener');
  };
  const mapQuery = event.latitude && event.longitude ? `${event.latitude},${event.longitude}` : encodeURIComponent(`${event.venueName}, ${event.address}`);

  return (
    <main className="container section event-details">
      <Link className="muted" to="/events">&lt; All events</Link>
      <div className="event-hero"><img src={poster(event)} alt={event.title} /></div>
      {event.status === 'Past' && <div className="panel event-ended">This event has ended. See what's on next at <Link className="gold" to={`/venues/resto-${event.restoId}`}>{event.venueName}</Link>.</div>}
      <span className="badge event-category">{event.category.toUpperCase()}</span>
      <h1>{event.title}</h1>
      <ul className="event-facts">
        <li><CalendarMonthOutlinedIcon />{eventWhen(event)}</li>
        <li><LocationOnOutlinedIcon /><span><Link className="gold" to={`/venues/resto-${event.restoId}`}>{event.venueName}</Link><br /><small className="muted">{event.address}</small></span></li>
        {event.entryInfo && <li><ConfirmationNumberOutlinedIcon />{event.entryInfo}</li>}
      </ul>
      <div className="event-actions">
        <Link className="btn btn-gold" to={`/venues/resto-${event.restoId}`}>View Venue</Link>
        <a className="btn btn-dark" href={`https://www.google.com/maps/dir/?api=1&destination=${mapQuery}`} target="_blank" rel="noreferrer">Get Directions</a>
        <button type="button" className="btn btn-dark" onClick={share}><ShareOutlinedIcon fontSize="small" /> Share</button>
      </div>
      {event.description && <><h2>About this event</h2><p className="event-description">{event.description}</p></>}
      {event.status !== 'Past' && (
        <section className="event-invite">
          <InviteToHangoutButton venueId={event.restoId} />
          <p className="muted">Going? Plan the night and invite your friends.</p>
        </section>
      )}
      <p className="muted" style={{ fontSize: 12, marginTop: 24 }}>
        Event details are provided by the venue. Entry, prices and age rules are set by the venue, which checks ID at the door. Please drink responsibly.
      </p>
    </main>
  );
}

// "Upcoming Events" on a venue's page; renders nothing when the venue has none.
export function VenueEventsSection({ restoId }: { restoId: number }) {
  const [events, setEvents] = useState<PublicEvent[]>([]);
  useEffect(() => {
    getVenueEvents(restoId).then(setEvents).catch(() => setEvents([]));
  }, [restoId]);
  if (events.length === 0) return null;
  return (
    <section className="venue-events">
      <h3>Upcoming Events</h3>
      {events.slice(0, 5).map((e) => (
        <Link key={e.id} to={`/events/${e.id}`} className="venue-event-row">
          <DateBlock iso={e.eventDate} />
          <span><b>{e.title}</b><small>{e.category} · {eventWhen(e)}</small></span>
        </Link>
      ))}
    </section>
  );
}
